import { useState, useEffect } from "react";

/**
 * Checks if the user is authenticated.
 * @returns {boolean} true if logged in, false otherwise.
 */
export const isAuthenticated = () => {
  try {
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const user = localStorage.getItem("user");
    const userCode = localStorage.getItem("userCode");
    const token = localStorage.getItem("token") || localStorage.getItem("access_token");

    return isLoggedIn === "true" || (!!user && user !== "undefined" && user !== "null") || !!userCode;
  } catch (err) {
    console.error("Error checking auth status:", err);
    return false;
  }
};

/**
 * Retrieves the currently logged-in user information from localStorage.
 * @returns {object|null}
 */
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem("user");
    if (userStr && userStr !== "undefined" && userStr !== "null") {
      return JSON.parse(userStr);
    }
  } catch (err) {
    console.error("Error parsing user from localStorage:", err);
  }
  return null;
};

/**
 * Sets user session in localStorage and broadcasts the event.
 * @param {object} userData - User info object
 * @param {string} [token] - Optional auth token
 */
export const setUserSession = (userData, token) => {
  try {
    localStorage.setItem("isLoggedIn", "true");
    if (userData) {
      localStorage.setItem("user", JSON.stringify(userData));
      if (userData.userCode || userData.patientId || userData.id) {
        localStorage.setItem("userCode", userData.userCode || userData.patientId || userData.id);
      }
      if (userData.name || userData.patientName) {
        localStorage.setItem("userName", userData.name || userData.patientName);
      }
    }
    if (token) {
      localStorage.setItem("token", token);
      localStorage.setItem("access_token", token);
    }

    // Broadcast change for other components & tabs
    window.dispatchEvent(new Event("authChange"));
  } catch (err) {
    console.error("Error setting user session:", err);
  }
};

/**
 * Clears user session from localStorage and broadcasts the event.
 */
export const clearUserSession = () => {
  try {
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("user");
    localStorage.removeItem("userCode");
    localStorage.removeItem("userName");
    localStorage.removeItem("token");
    // Broadcast change
    window.dispatchEvent(new Event("authChange"));
  } catch (err) {
    console.error("Error clearing user session:", err);
  }
};

/**
 * Custom React hook to subscribe to authentication state changes.
 * @returns {{ loggedIn: boolean, user: object|null, logout: Function, setSession: Function }}
 */
export const useAuth = () => {
  const [loggedIn, setLoggedIn] = useState(isAuthenticated());
  const [user, setUser] = useState(getCurrentUser());

  useEffect(() => {
    const updateAuth = () => {
      setLoggedIn(isAuthenticated());
      setUser(getCurrentUser());
    };

    window.addEventListener("authChange", updateAuth);
    window.addEventListener("storage", updateAuth);

    return () => {
      window.removeEventListener("authChange", updateAuth);
      window.removeEventListener("storage", updateAuth);
    };
  }, []);

  const logout = () => {
    clearUserSession();
  };

  const setSession = (userData, token) => {
    setUserSession(userData, token);
  };

  return { loggedIn, user, logout, setSession };
};
